import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

const LOAD_AHEAD = 240;

// The sidebar scrolls independently of the page. Observe its footer against
// that pane, and retain scroll/resize support when observers are unavailable.
export default function useSidebarAutoLoad({ scrollContainerRef, enabled, loadMore }) {
  const footerRef = useRef(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled) return;
    const pane = scrollContainerRef?.current;
    const footer = footerRef.current;
    if (!pane || !footer) return;
    let active = true;
    const checkPosition = () => {
      if (!active) return;
      const viewport = pane.getBoundingClientRect();
      const target = footer.getBoundingClientRect();
      if (viewport.height > 0 && target.height > 0
        && target.top <= viewport.bottom + LOAD_AHEAD && target.bottom >= viewport.top) {
        void loadMore();
      }
    };
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
      if (active && entries.some(entry => entry.isIntersecting)) void loadMore();
    }, { root: pane, rootMargin: `${LOAD_AHEAD}px 0px` });
    observer?.observe(footer);
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(checkPosition);
    resize?.observe(pane);
    pane.addEventListener('scroll', checkPosition, { passive: true });
    window.addEventListener('resize', checkPosition);
    checkPosition();
    return () => {
      active = false;
      observer?.disconnect();
      resize?.disconnect();
      pane.removeEventListener('scroll', checkPosition);
      window.removeEventListener('resize', checkPosition);
    };
  }, [scrollContainerRef, enabled, loadMore]);

  return footerRef;
}
